import { useState } from "react";
import { toast } from "sonner";
import { Telescope, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { US_STATES } from "../lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";

// Around Me: a deep dive across the whole web for restaurants (no food trucks) in one state.
const here = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) return reject(new Error("This device can't share its location"));
  navigator.geolocation.getCurrentPosition((p) => resolve(p.coords), () => reject(new Error("Allow location access so the deep dive can search near you")),
    { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 });
});

export const DeepDiveBar = ({ allowedStates, scans: allScans, onStarted, byState, userId, isDemo }) => {
  const scans = allScans.filter((s) => s.created_by === userId); // only your own dives
  const choices = Object.entries(US_STATES).map(([code, name]) => ({ code, name }))
    .filter((s) => !allowedStates?.length || allowedStates.includes(s.code));
  const [state, setState] = useState(choices.length === 1 ? choices[0].code : "");
  const [busy, setBusy] = useState(false);
  const [city, setCity] = useState("");
  const runningDeep = scans.find((s) => s.deep && s.status === "running");
  const last = scans.find((s) => s.deep && s.status !== "running");

  const start = async (near = !byState) => {
    setBusy(true);
    try {
      let body = { deep: true, state, city: city.trim() || null, categories: ["restaurants"] };
      if (near) {
        const c = await here();
        body = { deep: true, lat: c.latitude, lng: c.longitude, categories: ["restaurants"] };
      }
      const { data } = await api.post("/scans", body);
      onStarted(data);
      toast.success(!near ? `Deep dive started${city.trim() ? ` for ${city.trim()}` : " across 5 main cities"}. New restaurants appear here in a few minutes`
        : `Deep dive started within 50 miles of ${data.city}. New restaurants appear here in a few minutes`);
    } catch (e) { toast.error(e.response ? formatApiError(e) : e.message); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-slate-900 text-white p-4 sm:p-5 flex flex-wrap items-center gap-3" data-testid="deep-dive-bar">
      <Telescope className="h-6 w-6 text-blue-300" />
      <div className="mr-auto">
        <p className="font-display font-extrabold text-lg">Deep dive</p>
        <p className="text-xs text-slate-300">{byState ? "Finds restaurants with their own website (no food trucks, Toast or Shift4) in the state's 5 main cities, the city you type, or within 50 miles of you."
          : "Finds restaurants with their own website (no food trucks, Toast or Shift4) within 50 miles of where you are now."}</p>
        {runningDeep ? <p className="text-xs text-blue-200 mt-1" data-testid="deep-dive-status">Diving {runningDeep.near_me ? "within 50 miles of " : "into "}{runningDeep.city ? `${runningDeep.city}, ` : ""}{runningDeep.state}…</p>
          : last && <p className="text-xs text-slate-400 mt-1" data-testid="deep-dive-status">Last dive: {last.near_me ? "near " : ""}{last.city ? `${last.city}, ` : ""}{last.state} · {last.new} new · {last.status}</p>}
      </div>
      {byState && <>
      <Select value={state} onValueChange={setState}>
        <SelectTrigger className="w-48 rounded-full bg-white text-slate-900" data-testid="deep-dive-state"><SelectValue placeholder="Pick a state" /></SelectTrigger>
        <SelectContent>{choices.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}</SelectContent>
      </Select>
      <Input value={city} onChange={(e) => setCity(e.target.value)} maxLength={60} placeholder="City (optional)"
        className="w-44 rounded-full bg-white text-slate-900" data-testid="deep-dive-city" />
      </>}
      {isDemo && !allowedStates?.length && <p className="text-sm font-semibold text-amber-800" data-testid="demo-no-state">No state assigned yet. Ask Jeff to assign your state.</p>}
      <button onClick={() => start(!byState)} disabled={(byState && !state) || busy || !!runningDeep || (isDemo && !allowedStates?.length)} data-testid="deep-dive-button"
        className="h-11 px-6 rounded-full bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white font-semibold flex items-center gap-2 transition-colors">
        {(busy || runningDeep) && <Loader2 className="h-4 w-4 animate-spin" />}{byState ? "Deep dive" : "Deep dive near me"}
      </button>
      {byState && (
        <button onClick={() => start(true)} disabled={busy || !!runningDeep} data-testid="deep-dive-near-button"
          className="h-11 px-6 rounded-full bg-white text-slate-900 hover:bg-blue-50 disabled:opacity-50 font-semibold transition-colors">
          Near me (50 mi)
        </button>
      )}
    </div>
  );
};
